import React, { useEffect, useState } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography, 
  CircularProgress,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import { YamlService } from '../services/YamlService';
import './FileSelector.css';

interface FileSelectorProps {
  onFileSelect: (filename: string) => void;
  selectedFile: string | null;
}

const FileSelector: React.FC<FileSelectorProps> = ({ onFileSelect, selectedFile }) => {
  const [files, setFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFiles = async () => {
      try {
        setLoading(true);
        const availableFiles = await YamlService.getAvailableFiles();
        setFiles(availableFiles);
        setError(null);
      } catch (err) {
        setError('Failed to load available files');
        console.error('Error loading files:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFiles();
  }, []);

  const handleFileChange = (event: SelectChangeEvent) => {
    const filename = event.target.value;
    onFileSelect(filename);
  };

  const formatFileName = (fileName: string) => {
    return fileName
      .replace('.yaml', '')
      .replace(/_/g, ' ')
      .replace(/^\d+_/, '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="file-selector">
      <Typography variant="h6" className="selector-title">
        Select Workflow Template
      </Typography>
      {loading ? (
        <div className="loading-container">
          <CircularProgress size={24} />
          <Typography variant="body2">Loading templates...</Typography>
        </div>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel id="workflow-template-label">Workflow Template</InputLabel>
          <Select
            labelId="workflow-template-label"
            id="workflow-template"
            value={selectedFile || ''}
            onChange={handleFileChange}
            label="Workflow Template"
            disabled={loading}
          >
            <MenuItem value="">
              <em>-- Select a template --</em>
            </MenuItem>
            {files.map((file) => (
              <MenuItem key={file} value={file}>
                {formatFileName(file)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </div>
  );
};

export default FileSelector; 